'use client'

import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useRouter } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { Box, Typography, Divider } from '@mui/material'

import { useWallet } from '@/src/hooks/wallet'
import { formatCurrency } from '@/src/utils/currency/formatCurrency'
import { formatTransactionForDisplay } from '@/src/utils/wallet'
import { getMobileDeviceType } from '@/src/utils/deviceType'
import useRedirectToHome from '@/src/hooks/navigation/useRedirectToHome'
import useRedirectFromWallet from '@/src/hooks/navigation/useRedirectFromWallet'
import SwipeableDrawerWithBackNavigation from '@/src/components/common/ui/swipeableDrawer/swipeableDrawer'
import { SOURCE_TYPE_ICON_TYPES } from '@/src/constants/wallet'

import { WalletPage } from './wallet'
import { WalletPageShimmer } from './walletShimmer'
import { WalletFAQ } from './walletFAQ'
import BottomToolbarWrapper from '../bottomToolbar'

interface WalletPageWrapperProps {
    isAppWallet?: boolean
}

// Dynamically load SignInModal to reduce initial bundle size
const SignInModalWrapper = dynamic(
    () =>
        import(
            /* webpackChunkName: "signin-modal" */ '@/src/components/common/flyinHeader/SignInModal/SignInModalWrapper'
        ),
    { ssr: false }
)

const WalletPageWrapper: React.FC<WalletPageWrapperProps> = ({ isAppWallet = false }) => {
    const router = useRouter()
    const locale = useLocale()
    const t = useTranslations('wallet')
    const tCommon = useTranslations('common')

    // Track if component is mounted (client-side only)
    const [isMounted, setIsMounted] = useState(false)
    const [modalOpen, setModalOpen] = useState(false)
    const [domainBalanceSheetOpen, setDomainBalanceSheetOpen] = useState(false)
    const [faqDrawerOpen, setFaqDrawerOpen] = useState(false)

    // Use wallet hook
    const wallet = useWallet()
    const goToHome = useRedirectToHome()
    const goBackFromWallet = useRedirectFromWallet()

    // Check if device is Android or iOS app
    const mobileDeviceType = isMounted ? getMobileDeviceType() : null
    const isNativeApp = mobileDeviceType === 'AndroidApp' || mobileDeviceType === 'iPhoneApp'

    // Initialize on mount
    useEffect(() => {
        if (typeof window === 'undefined') return
        setIsMounted(true)
    }, [])

    // Redirect to sign-in if not logged in
    useEffect(() => {
        if (isMounted && !wallet.isLoggedIn) {
            setModalOpen(true)
        }
    }, [isMounted, wallet.isLoggedIn])

    // Handlers
    const handleModalClose = useCallback(() => {
        setModalOpen(false)
        // If still not logged in, redirect to account page
        if (!wallet.isLoggedIn) {
            router.push(`/${locale}/account`)
        }
    }, [wallet.isLoggedIn, router, locale])

    const handleOpenDomainBalanceSheet = useCallback(() => {
        setDomainBalanceSheetOpen(true)
    }, [])

    const handleCloseDomainBalanceSheet = useCallback(() => {
        setDomainBalanceSheetOpen(false)
    }, [])

    const handleOpenFAQDrawer = useCallback(() => {
        setFaqDrawerOpen(true)
    }, [])

    const handleCloseFAQDrawer = useCallback(() => {
        setFaqDrawerOpen(false)
    }, [])

    const handleGoToHome = useCallback(() => {
        // If appWallet is true, use wallet callback to go back to app
        if (isAppWallet || isNativeApp) {
            goBackFromWallet({ isReloadRequired: false })
        } else {
            goToHome({ isReloadRequired: false })
        }
    }, [goToHome, goBackFromWallet, isAppWallet, isNativeApp])

    // Format amount helper
    const formatAmount = useCallback(
        (amount: number, currency: string) =>
            formatCurrency({
                amount,
                currency,
                locale,
                precision: 2
            }),
        [locale]
    )

    // Format transactions for display (business logic in logic layer)
    const formattedTransactions = useMemo(
        () =>
            wallet.transactions.map(txn => {
                const formatted = formatTransactionForDisplay(txn, t)
                // Add icon type based on source type (no tab checking needed)
                const iconType = SOURCE_TYPE_ICON_TYPES[txn.sourceType] || 'card'
                return {
                    ...formatted,
                    iconType
                }
            }),
        [wallet.transactions, t]
    )

    // Translate error messages
    const translatedBalanceError = useMemo(() => {
        if (!wallet.balanceError) return null
        // If it's the hardcoded message, use translation
        if (wallet.balanceError === 'Unable to fetch wallet balance') {
            return t('errors.balanceFetchFailed')
        }
        return wallet.balanceError
    }, [wallet.balanceError, t])

    const translatedTransactionsError = useMemo(() => {
        if (!wallet.transactionsError) return null
        // If it's the hardcoded message, use translation
        if (wallet.transactionsError === 'Unable to fetch transactions') {
            return t('errors.transactionsFetchFailed')
        }
        return wallet.transactionsError
    }, [wallet.transactionsError, t])

    // Determine if we should show full page shimmer
    const showFullPageShimmer = wallet.isLoadingBalance && wallet.isLoadingTransactions

    // Don't render until mounted to prevent hydration mismatch
    if (!isMounted) {
        return null
    }

    return (
        <Box
            sx={{
                minHeight: '100dvh',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.default'
            }}
        >
            {/* Main Content */}
            <Box sx={{ flex: 1 }}>
                {showFullPageShimmer ? (
                    <WalletPageShimmer showBackButton={true} />
                ) : (
                    <WalletPage
                        displayBalance={wallet.displayBalance}
                        displayCurrency={wallet.displayCurrency}
                        domainBalances={wallet.domainBalances}
                        hasMultipleDomainBalances={wallet.hasMultipleDomainBalances}
                        formattedTransactions={formattedTransactions}
                        hasMoreTransactions={wallet.hasMoreTransactions}
                        activeTab={wallet.activeTab}
                        onTabChange={wallet.setActiveTab}
                        isLoadingBalance={wallet.isLoadingBalance}
                        isLoadingTransactions={wallet.isLoadingTransactions}
                        isFetchingMore={wallet.isFetchingTransactions}
                        balanceError={translatedBalanceError}
                        transactionsError={translatedTransactionsError}
                        onLoadMore={wallet.loadMoreTransactions}
                        onRefresh={wallet.refreshWallet}
                        onGoToHome={handleGoToHome}
                        showBackButton={true}
                        isDomainBalanceSheetOpen={domainBalanceSheetOpen}
                        onOpenDomainBalanceSheet={handleOpenDomainBalanceSheet}
                        onCloseDomainBalanceSheet={handleCloseDomainBalanceSheet}
                        isFAQDrawerOpen={faqDrawerOpen}
                        onOpenFAQDrawer={handleOpenFAQDrawer}
                        onCloseFAQDrawer={handleCloseFAQDrawer}
                        formatAmount={formatAmount}
                        t={t}
                        tCommon={tCommon}
                        locale={locale}
                    />
                )}

                {/* Hide bottom toolbar when appWallet is true */}
                {!isAppWallet && <BottomToolbarWrapper />}
            </Box>

            {/* Sign In Modal */}
            {modalOpen && <SignInModalWrapper open={modalOpen} onClose={handleModalClose} />}

            {/* Domain Balances Sheet */}
            <SwipeableDrawerWithBackNavigation
                open={domainBalanceSheetOpen}
                onClose={handleCloseDomainBalanceSheet}
                sx={{ height: 'auto', maxHeight: '70dvh' }}
            >
                <Box sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                        {t('domainBalances')}
                    </Typography>

                    {/* Table Header */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 2,
                            mb: 2,
                            pb: 1.5,
                            borderBottom: 2,
                            borderColor: 'divider'
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            {t('currency')}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            {t('balance')}
                        </Typography>
                    </Box>

                    {/* Domain Balances List */}
                    <Box sx={{ mb: 3 }}>
                        {wallet.domainBalances.map((db, index) => {
                            const isCurrentDomain = db.currency === wallet.displayCurrency
                            return (
                                <Box
                                    key={db.domain || db.currency}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: 2,
                                        py: 1.5,
                                        borderBottom: index < wallet.domainBalances.length - 1 ? 1 : 0,
                                        borderColor: 'divider'
                                    }}
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontWeight: isCurrentDomain ? 700 : 400,
                                            color: isCurrentDomain ? 'text.primary' : 'text.secondary'
                                        }}
                                    >
                                        {db.currency}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontWeight: isCurrentDomain ? 700 : 400,
                                            color: isCurrentDomain ? 'text.primary' : 'text.secondary'
                                        }}
                                    >
                                        {formatAmount(db.balance, db.currency)}
                                    </Typography>
                                </Box>
                            )
                        })}
                    </Box>

                    {/* Description Text */}
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            fontSize: '0.875rem',
                            lineHeight: 1.6,
                            mt: 2
                        }}
                    >
                        {t('domainBalancesDescription')}
                    </Typography>
                </Box>
            </SwipeableDrawerWithBackNavigation>

            {/* FAQ Drawer */}
            <SwipeableDrawerWithBackNavigation
                open={faqDrawerOpen}
                onClose={handleCloseFAQDrawer}
                sx={{ height: 'auto', maxHeight: '90dvh' }}
            >
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                    <WalletFAQ />
                </Box>
            </SwipeableDrawerWithBackNavigation>
        </Box>
    )
}

export { WalletPageWrapper }
export default WalletPageWrapper
